import React from "react";
import { Paper } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";

/* ------------------------------------------------------------------
   Presentation helpers for the Windows & Doors pages (exterior orders,
   window RFQs, lead times, service requests, door builder).

   Everything here is theme-aware through the .app-* helpers and CSS
   tokens in index.css, so it reads in classic and Liquid Glass, light and
   dark, without a theme read.
------------------------------------------------------------------ */

// Translucent status tints — they sit on glass without turning into opaque
// light blocks. Literal strings so Tailwind keeps them.
export const TINT = {
  sky: "bg-sky-500/15 text-sky-700 dark:text-sky-300",
  blue: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
  indigo: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300",
  violet: "bg-violet-500/15 text-violet-700 dark:text-violet-300",
  fuchsia: "bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300",
  emerald: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  teal: "bg-teal-500/15 text-teal-700 dark:text-teal-300",
  amber: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  yellow: "bg-yellow-500/20 text-yellow-800 dark:text-yellow-300",
  orange: "bg-orange-500/15 text-orange-700 dark:text-orange-300",
  rose: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
  red: "bg-red-500/15 text-red-700 dark:text-red-300",
  slate: "bg-slate-500/15 text-slate-600 dark:text-slate-300",
};

/** Small rounded status/label pill. */
export const Pill = ({ tone = "slate", className = "", children, ...rest }) => (
  <span
    className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${
      TINT[tone] || TINT.slate
    } ${className}`}
    {...rest}
  >
    {children}
  </span>
);

/** Age tone: 7+ days rose, 3+ amber, otherwise neutral. */
export const waitTone = (days) =>
  days >= 7 ? "rose" : days >= 3 ? "amber" : "slate";

// Inline notices (saved / failed / heads-up).
const BANNER_TONES = {
  success:
    "border-emerald-500/25 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200",
  error: "border-rose-500/25 bg-rose-500/10 text-rose-700 dark:text-rose-300",
  warning:
    "border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-200",
  info: "border-sky-500/25 bg-sky-500/10 text-sky-800 dark:text-sky-200",
};

export const Banner = ({ tone = "info", className = "", children }) => (
  <div
    role={tone === "error" ? "alert" : "status"}
    className={`rounded-xl border px-4 py-2.5 text-sm ${
      BANNER_TONES[tone] || BANNER_TONES.info
    } ${className}`}
  >
    {children}
  </div>
);

/** Themed text input / select / textarea class. */
export const inputCls =
  "app-input w-full rounded-xl border px-3 py-2 text-sm transition disabled:cursor-not-allowed disabled:opacity-50";

/** Same, compact (table cells, toolbars). */
export const inputSmCls =
  "app-input rounded-lg border px-2.5 py-1.5 text-sm transition disabled:cursor-not-allowed disabled:opacity-50";

/**
 * iOS-style segmented control (matches the MUI ToggleButtonGroup look).
 * options: [{ key, label, badge? }]
 */
export const Segmented = ({
  options,
  value,
  onChange,
  ariaLabel,
  className = "",
  fill = false,
}) => (
  <div
    role="tablist"
    aria-label={ariaLabel}
    className={`${
      fill ? "flex w-full" : "inline-flex max-w-full"
    } flex-wrap gap-0.5 rounded-xl bg-[color:var(--app-hover)] p-[3px] ${className}`}
  >
    {options.map((option) => {
      const active = option.key === value;
      return (
        <button
          key={option.key}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange(option.key)}
          className={`inline-flex min-h-[34px] items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-3.5 py-1.5 text-sm font-semibold transition ${
            fill ? "flex-1" : ""
          } ${
            active
              ? "bg-white text-[color:var(--app-text)] shadow-sm dark:bg-white/[0.14] dark:shadow-none"
              : "app-muted hover:text-[color:var(--app-text)]"
          }`}
        >
          {option.label}
          {option.badge}
        </button>
      );
    })}
  </div>
);

/** Filter chip (toggle). Count renders as a muted tabular suffix. */
export const FilterChip = ({ active, onClick, children, count }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`inline-flex min-h-[32px] items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-semibold transition ${
      active
        ? "border-transparent bg-[color:var(--app-accent-solid)] text-white"
        : "app-surface text-[color:var(--app-text)] hover:bg-[color:var(--app-hover)]"
    }`}
  >
    {children}
    {count !== undefined && (
      <span
        className={`tabular-nums ${active ? "text-white/80" : "app-muted"}`}
      >
        {count}
      </span>
    )}
  </button>
);

/** "Back to …" link that sits above a PageHeader. */
export const BackLink = ({ onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="app-muted -ml-2 mb-3 inline-flex min-h-[36px] items-center gap-0.5 rounded-full px-2 text-sm font-semibold transition hover:bg-[color:var(--app-hover)] hover:text-[color:var(--app-text)]"
  >
    <ChevronLeftIcon fontSize="small" />
    {children}
  </button>
);

/* ---------------- material-table ----------------
   Lets a material-table sit flush inside an `app-card overflow-hidden
   rounded-2xl border` wrapper: transparent container, tokenized header and
   search colors (CSS variables, so no theme read is needed). Defined at
   module scope so the Container isn't a new component every render. */
const MTContainer = (props) => <Paper {...props} elevation={0} />;
export const mtComponents = { Container: MTContainer };

export const mtStyle = {
  backgroundColor: "transparent",
  color: "var(--app-text)",
  boxShadow: "none",
};

export const mtHeaderStyle = {
  backgroundColor: "var(--app-surface-bg)",
  color: "var(--app-text-muted)",
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: "0.02em",
  whiteSpace: "nowrap",
  borderBottom: "1px solid var(--app-divider)",
};

export const mtSearchFieldStyle = { color: "var(--app-text)" };

export const mtRowStyle = () => ({ fontSize: 14 });
